'use client';

import React, { useState } from 'react';
import { Pencil, Send, CheckCircle, AlertCircle, FileText, Plus, Search, Filter, Trash2, DollarSign, TrendingUp, Clock } from 'lucide-react';
import { invoicesService, type Invoice } from '@/lib/services/staffingService';

const statusConfig = {
  draft: { label: 'Draft', icon: FileText, className: 'status-draft' },
  sent: { label: 'Sent', icon: Send, className: 'status-open' },
  paid: { label: 'Paid', icon: CheckCircle, className: 'status-approved' },
  overdue: { label: 'Overdue', icon: AlertCircle, className: 'status-expired' },
};

interface InvoiceListProps {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
  onEdit: (invoice: Invoice) => void;
  onCreateNew: () => void;
  onStatusChange: () => void;
}

export default function InvoiceList({ invoices, loading, error, onEdit, onCreateNew, onStatusChange }: InvoiceListProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const filtered = invoices.filter((inv) => {
    const matchSearch =
      inv.client.toLowerCase().includes(search.toLowerCase()) ||
      inv.invoiceNumber.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalOutstanding = invoices
    .filter((i) => i.status === 'sent' || i.status === 'overdue')
    .reduce((sum, i) => sum + i.total, 0);
  const totalPaid = invoices.filter((i) => i.status === 'paid').reduce((sum, i) => sum + i.total, 0);
  const overdueCount = invoices.filter((i) => i.status === 'overdue').length;
  const totalBillableHours = invoices.reduce((sum, inv) => sum + inv.lineItems.reduce((s, li) => s + li.hours, 0), 0);

  const handleMarkPaid = async (inv: Invoice) => {
    setUpdatingId(inv.id);
    try {
      await invoicesService.update(inv.id, { status: 'paid' });
      onStatusChange();
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMarkSent = async (inv: Invoice) => {
    setUpdatingId(inv.id);
    try {
      await invoicesService.update(inv.id, { status: 'sent' });
      onStatusChange();
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (inv: Invoice) => {
    if (!confirm(`Delete invoice ${inv.invoiceNumber}?`)) return;
    setUpdatingId(inv.id);
    try {
      await invoicesService.delete(inv.id);
      onStatusChange();
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
              <div className="h-3 bg-muted rounded w-24 mb-2" />
              <div className="h-7 bg-muted rounded w-20 mb-1" />
              <div className="h-3 bg-muted rounded w-16" />
            </div>
          ))}
        </div>
        <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground text-sm animate-pulse">
          Loading invoices...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <AlertCircle size={32} className="text-danger mx-auto mb-3" />
        <p className="text-sm text-danger font-600">{error}</p>
        <button onClick={onStatusChange} className="mt-3 text-xs text-primary hover:underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={14} className="text-muted-foreground" />
            <p className="text-xs text-muted-foreground font-500">Total Invoices</p>
          </div>
          <p className="text-2xl font-700 text-foreground font-mono-data">{invoices.length}</p>
          <p className="text-xs text-muted-foreground mt-1">This period</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} className="text-warning" />
            <p className="text-xs text-muted-foreground font-500">Outstanding</p>
          </div>
          <p className="text-2xl font-700 text-warning font-mono-data">${totalOutstanding.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">Awaiting payment</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-positive" />
            <p className="text-xs text-muted-foreground font-500">Collected</p>
          </div>
          <p className="text-2xl font-700 text-positive font-mono-data">${totalPaid.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">Paid invoices</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={14} className="text-primary" />
            <p className="text-xs text-muted-foreground font-500">Billable Hours</p>
          </div>
          <p className="text-2xl font-700 text-primary font-mono-data">{totalBillableHours.toFixed(0)}h</p>
          <p className="text-xs text-muted-foreground mt-1">{overdueCount > 0 ? <span className="text-danger">{overdueCount} overdue</span> : 'All current'}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Filter size={14} className="text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm bg-muted border border-border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <button
            onClick={onCreateNew}
            className="ml-auto btn-primary flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-600"
          >
            <Plus size={14} />
            New
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Client</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Period</th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Hours</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Due Date</th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="text-right px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">
                    No invoices found
                  </td>
                </tr>
              ) : (
                filtered.map((inv) => {
                  const cfg = statusConfig[inv.status];
                  const StatusIcon = cfg.icon;
                  const billableHours = inv.lineItems.reduce((s, li) => s + li.hours, 0);
                  const isUpdating = updatingId === inv.id;
                  return (
                    <tr key={inv.id} className={`row-hover transition-colors ${isUpdating ? 'opacity-50' : ''}`}>
                      <td className="px-5 py-3.5 font-mono-data text-xs font-600 text-primary">{inv.invoiceNumber}</td>
                      <td className="px-4 py-3.5">
                        <p className="font-500 text-foreground">{inv.client}</p>
                        <p className="text-xs text-muted-foreground">{inv.clientEmail}</p>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground font-mono-data">
                        {inv.periodStart} – {inv.periodEnd}
                      </td>
                      <td className="px-4 py-3.5 text-right font-mono-data text-xs text-muted-foreground">
                        {billableHours > 0 ? `${billableHours.toFixed(1)}h` : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground font-mono-data">{inv.dueDate}</td>
                      <td className="px-4 py-3.5 text-right font-mono-data font-600 text-foreground">
                        ${inv.total.toLocaleString()}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-600 ${cfg.className}`}>
                          <StatusIcon size={11} />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {inv.status === 'draft' && (
                            <button
                              onClick={() => handleMarkSent(inv)}
                              disabled={isUpdating}
                              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                              title="Mark as Sent"
                            >
                              <Send size={14} />
                            </button>
                          )}
                          {(inv.status === 'sent' || inv.status === 'overdue') && (
                            <button
                              onClick={() => handleMarkPaid(inv)}
                              disabled={isUpdating}
                              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-positive transition-colors"
                              title="Mark as Paid"
                            >
                              <CheckCircle size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => onEdit(inv)}
                            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            title="Edit invoice"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(inv)}
                            disabled={isUpdating}
                            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-danger transition-colors"
                            title="Delete invoice"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Financial Reconciliation Footer */}
        {invoices.length > 0 && (
          <div className="border-t border-border px-5 py-4 bg-muted/20">
            <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-3">Financial Reconciliation</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Total Invoiced</p>
                <p className="text-sm font-700 text-foreground font-mono-data">
                  ${invoices.reduce((s, i) => s + i.total, 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Collected</p>
                <p className="text-sm font-700 text-positive font-mono-data">${totalPaid.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Outstanding</p>
                <p className="text-sm font-700 text-warning font-mono-data">${totalOutstanding.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Collection Rate</p>
                <p className="text-sm font-700 text-foreground font-mono-data">
                  {invoices.reduce((s, i) => s + i.total, 0) > 0
                    ? `${Math.round((totalPaid / invoices.reduce((s, i) => s + i.total, 0)) * 100)}%`
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
